import type {PullRequestPickerPullRequest} from '@github-ui/item-picker/PullRequestAndBranchPicker'
import {type ConnectionOperation, DOCUMENT_FIELDS_PATH, type EntityOperation} from '@github-ui/optimistic-connection'
import {
  createConnectionMutationId,
  dispatchConnectionMutation,
  publishOptimisticFields,
} from '@github-ui/optimistic-connection/connectionMutationBridge'
import {commitMutation, type Environment, graphql} from 'react-relay'

import type {
  linkPullRequestsMutation,
  linkPullRequestsMutation$data,
  linkPullRequestsMutation$rawResponse,
} from './__generated__/linkPullRequestsMutation.graphql'

type LinkPullRequestsMutationParams = {
  environment: Environment
  input: {baseIssueOrPullRequestId: string; linkingIds: string[]}
  onError: (error: Error) => void
  onCompleted?: () => void
  linkingPrs: PullRequestPickerPullRequest[]
  changes?: {added: PullRequestPickerPullRequest[]; removed: PullRequestPickerPullRequest[]}
}

function toLinkedPullRequestsOperation(
  data: linkPullRequestsMutation$data | linkPullRequestsMutation$rawResponse,
  baseIssueOrPullRequestId: string,
): ConnectionOperation | undefined {
  const baseIssueOrPullRequest = data.linkIssueOrPullRequest?.baseIssueOrPullRequest
  if (!baseIssueOrPullRequest || !('closedByPullRequestsReferences' in baseIssueOrPullRequest)) return undefined
  return {
    type: 'setFields',
    id: baseIssueOrPullRequestId,
    fields: {closedByPullRequestsReferences: baseIssueOrPullRequest.closedByPullRequestsReferences},
  }
}

function toLinkedPullRequestItemsOperation({
  baseIssueOrPullRequestId,
  added,
  removed,
}: {
  baseIssueOrPullRequestId: string
  added: ReadonlyArray<PullRequestPickerPullRequest | Record<string, unknown>>
  removed: readonly PullRequestPickerPullRequest[]
}): EntityOperation | undefined {
  if (added.length === 0 && removed.length === 0) return undefined
  const upserts = added.flatMap(item => {
    const url = item.url
    return typeof url === 'string' ? [{key: url, item}] : []
  })
  const removeKeys = removed.map(item => item.url).filter((url): url is string => typeof url === 'string')
  return {
    type: 'updateEntityFieldItems',
    id: baseIssueOrPullRequestId,
    field: 'linkedPullRequests',
    upserts,
    removeKeys,
    countDelta: added.length - removed.length,
  }
}

export function linkPullRequestsMutation({
  environment,
  input: {baseIssueOrPullRequestId, linkingIds},
  onError,
  onCompleted,
  linkingPrs,
  changes,
}: LinkPullRequestsMutationParams) {
  const optimisticResponse: linkPullRequestsMutation$rawResponse = {
    linkIssueOrPullRequest: {
      baseIssueOrPullRequest: {
        __typename: 'Issue',
        id: baseIssueOrPullRequestId,
        __isNode: 'Issue',
        closedByPullRequestsReferences: {
          nodes: (linkingPrs || []).map(pr => {
            const {url, number, title, isDraft, isInMergeQueue, state, repository, id: pullRequestId, createdAt} = pr
            const {id: repoId, name, nameWithOwner, owner} = repository
            const {login, __typename, id: ownerId} = owner

            return {
              id: pullRequestId,
              createdAt,
              isDraft,
              isInMergeQueue,
              number,
              url,
              title,
              state,
              __typename: 'PullRequest',
              repository: {
                id: repoId,
                name,
                nameWithOwner,
                owner: {
                  id: ownerId,
                  login,
                  __typename,
                },
              },
            }
          }),
        },
      },
    },
  }

  const mutationId = createConnectionMutationId()
  const optimisticOperation = toLinkedPullRequestsOperation(optimisticResponse, baseIssueOrPullRequestId)
  const optimisticItemsOperation = changes
    ? toLinkedPullRequestItemsOperation({baseIssueOrPullRequestId, ...changes})
    : undefined
  const itemsPublication = optimisticItemsOperation ? publishOptimisticFields(optimisticItemsOperation) : undefined

  const dispatchOperationError = (error: Error) => dispatchConnectionMutation({phase: 'error', mutationId, error})

  // Published before `commitMutation`: a same-tick terminal would find nothing pending to settle.
  if (optimisticOperation) {
    dispatchConnectionMutation({
      phase: 'optimistic',
      mutationId,
      ownerId: baseIssueOrPullRequestId,
      connectionPath: DOCUMENT_FIELDS_PATH,
      operation: optimisticOperation,
    })
  }

  let disposable
  try {
    disposable = commitMutation<linkPullRequestsMutation>(environment, {
      // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
      mutation: graphql`
        mutation linkPullRequestsMutation($baseIssueOrPullRequestId: ID!, $linkingIds: [ID!]!) @raw_response_type {
          linkIssueOrPullRequest(
            input: {baseIssueOrPullRequestId: $baseIssueOrPullRequestId, linkingIds: $linkingIds}
          ) {
            baseIssueOrPullRequest {
              ... on Issue {
                closedByPullRequestsReferences(first: 10, includeClosedPrs: true) {
                  nodes {
                    ... on PullRequest {
                      id
                      url
                      number
                      title
                      state
                      isDraft
                      isInMergeQueue
                      createdAt
                      repository {
                        id
                        name
                        nameWithOwner
                        owner {
                          id
                          login
                          __typename
                        }
                      }
                      __typename
                    }
                  }
                }
              }
            }
          }
        }
      `,
      variables: {baseIssueOrPullRequestId, linkingIds},
      optimisticResponse,
      onError: error => {
        dispatchOperationError(error)
        itemsPublication?.failed(error)
        onError(error)
      },
      onUnsubscribe: () => {
        dispatchOperationError(new Error('Linking pull requests was cancelled'))
        itemsPublication?.failed(new Error('Linking pull requests was cancelled'))
      },
      onCompleted: (response, errors) => {
        const operation = toLinkedPullRequestsOperation(response, baseIssueOrPullRequestId)
        if (!operation) {
          // The overlay rolls back, so reporting success would close the picker over a link that
          // never happened.
          const error = new Error(errors?.[0]?.message ?? 'Failed to link pull requests')
          dispatchOperationError(error)
          itemsPublication?.failed(error)
          onError(error)
          return
        }
        dispatchConnectionMutation({
          phase: 'completed',
          mutationId,
          ownerId: baseIssueOrPullRequestId,
          connectionPath: DOCUMENT_FIELDS_PATH,
          operation,
        })
        if (optimisticItemsOperation && changes) {
          const confirmedNodes = response.linkIssueOrPullRequest?.baseIssueOrPullRequest
          const nodes =
            confirmedNodes && 'closedByPullRequestsReferences' in confirmedNodes
              ? (confirmedNodes.closedByPullRequestsReferences?.nodes ?? [])
              : []
          const confirmedAdded = changes.added.map(
            added => nodes.find(node => node?.id === added.id) ?? added,
          ) as Array<PullRequestPickerPullRequest | Record<string, unknown>>
          const confirmedItemsOperation = toLinkedPullRequestItemsOperation({
            baseIssueOrPullRequestId,
            added: confirmedAdded,
            removed: changes.removed,
          })
          if (confirmedItemsOperation) itemsPublication?.completed(confirmedItemsOperation)
        }
        onCompleted?.()
      },
    })
  } catch (error) {
    dispatchOperationError(error instanceof Error ? error : new Error('Failed to link pull requests'))
    itemsPublication?.failed(error instanceof Error ? error : new Error('Failed to link pull requests'))
    throw error
  }

  return disposable
}
